import {
  LevelType,
  Room,
  RoomGroups,
  RoomGroupType,
  RoomSummary,
} from './types';
import { PropertyField, RoomField } from '../../../../system/field';
import { findNextPositiveNumberFromArray } from '../../../../utils';
export { arrayMove } from '@dnd-kit/sortable';

export const getRoomsCounter = (rooms: Room[]): RoomGroupType[] => {
  const roomTypesCounter = rooms.reduce((acc: any, room: Room) => {
    const roomType = room[RoomField.ROOM_TYPE].toLowerCase();

    RoomGroups.forEach((group) => {
      const matches = [group.type, ...(group.groupWith || [])].filter((g) =>
        roomType.includes(g.toLowerCase()),
      );
      if (matches.length) {
        acc[group.type] = acc[group.type] ? acc[group.type] + 1 : 1;
      }
    });

    return acc;
  }, {});

  return RoomGroups.map((group) => {
    return { ...group, count: roomTypesCounter[group.type] ?? 0 };
  });
};

export const getNextItemNumber = (existingRooms: Room[]): number => {
  if (!existingRooms.length) return 1;

  const maxItemNumber = Math.max(
    ...existingRooms.map((room) => Number(room.ROOM_ITEM_NUMBER || 0)),
  );

  return maxItemNumber + 1;
};

const getRoomType = (roomType: string) => {
  const type = roomType.toLowerCase();
  return {
    isBedroom: type.includes('bedroom'),
    isFullBath:
      (type.includes('bathroom') || type.includes('full bath')) &&
      !type.includes('half bath'),
    isHalfBath: type.includes('half bath'),
  };
};

export const normalizeLevel = (
  level: string,
): { type: LevelType; number?: number } => {
  const normalized = level?.toLowerCase() || '';
  if (
    normalized === LevelType.UNSPECIFIED.toLowerCase() ||
    normalized === LevelType.MAIN.toLowerCase()
  ) {
    return { type: LevelType.MAIN };
  }

  const match = normalized.match(/(upper|lower)\s*(\d+)/);
  if (!match) return { type: LevelType.MAIN };

  const typeString = match[1]; // "upper" or "lower"
  let determinedType: LevelType;

  if (typeString === 'upper') {
    determinedType = LevelType.UPPER;
  } else if (typeString === 'lower') {
    determinedType = LevelType.LOWER;
  } else {
    // This case should not be reached with the current regex
    determinedType = LevelType.MAIN; // Fallback
  }

  return {
    type: determinedType,
    number: parseInt(match[2]),
  };
};

export const matchesCounteredRoomType = (roomType: string) => {
  const matches = roomType.match(/^(bedroom|bathroom)(\s*(\d+))?/i);
  return matches;
};

export const normalizeRoomType = (
  roomType: string,
): { type: string; number?: number; isNumberedRoomType: boolean } => {
  const matches = matchesCounteredRoomType(roomType);
  if (!matches) return { type: roomType, isNumberedRoomType: false };

  const typeString = matches[1]; // "bedroom" or "bathroom"

  return {
    type: typeString,
    number: matches[2] ? parseInt(matches[2]) : 0,
    isNumberedRoomType: true,
  };
};

export const getRoomSummaryUpdates = (summary: RoomSummary) => {
  let updates: any = {
    [PropertyField.STORY_LIST]: summary.storyList,

    // Main level
    [PropertyField.MAIN_LEVEL_BEDROOMS]: summary.mainLevel.bedrooms,
    [PropertyField.MAIN_LEVEL_FULL_BATHS]: summary.mainLevel.fullBaths,
    [PropertyField.MAIN_LEVEL_HALF_BATHS]: summary.mainLevel.halfBaths,
  };

  // Add all upper level fields
  for (let i = 1; i <= 6; i++) {
    const upperCounts = summary.upperLevels[i];

    const update =
      upperCounts.hasRoom ||
      upperCounts.bedrooms > 0 ||
      upperCounts.fullBaths > 0 ||
      upperCounts.halfBaths > 0;
    if (!update) {
      updates[`UPPER_LEVEL_${i}_BEDROOMS` as PropertyField] = null;
      updates[`UPPER_LEVEL_${i}_FULL_BATHS` as PropertyField] = null;
      updates[`UPPER_LEVEL_${i}_HALF_BATHS` as PropertyField] = null;
      continue;
    }

    updates[`UPPER_LEVEL_${i}_BEDROOMS` as PropertyField] =
      upperCounts.bedrooms;
    updates[`UPPER_LEVEL_${i}_FULL_BATHS` as PropertyField] =
      upperCounts.fullBaths;
    updates[`UPPER_LEVEL_${i}_HALF_BATHS` as PropertyField] =
      upperCounts.halfBaths;
  }

  // Add all lower level fields
  for (let i = 1; i <= 6; i++) {
    const lowerCounts = summary.lowerLevels[i];

    const update =
      lowerCounts.hasRoom ||
      lowerCounts.bedrooms > 0 ||
      lowerCounts.fullBaths > 0 ||
      lowerCounts.halfBaths > 0;
    if (!update) {
      updates[`LOWER_LEVEL_${i}_BEDROOMS` as PropertyField] = null;
      updates[`LOWER_LEVEL_${i}_FULL_BATHS` as PropertyField] = null;
      updates[`LOWER_LEVEL_${i}_HALF_BATHS` as PropertyField] = null;
      continue;
    }

    updates[`LOWER_LEVEL_${i}_BEDROOMS` as PropertyField] =
      lowerCounts.bedrooms;
    updates[`LOWER_LEVEL_${i}_FULL_BATHS` as PropertyField] =
      lowerCounts.fullBaths;
    updates[`LOWER_LEVEL_${i}_HALF_BATHS` as PropertyField] =
      lowerCounts.halfBaths;
  }

  // set totals last
  updates = {
    ...updates,
    [PropertyField.BEDROOMS_TOTAL]: summary.totalBedrooms,
    [PropertyField.BATHROOMS_FULL]: summary.totalFullBaths,
    [PropertyField.BATHROOMS_HALF]: summary.totalHalfBaths,
    [PropertyField.BATHROOMS_TOTAL_INTEGER]: summary.totalBathrooms,
    [PropertyField.ROOMS_TOTAL]: summary.totalRooms,
  };
  return updates;
};

export const getNextLevel = (
  rooms: Room[],
  emptyLevels: string[],
  levelType: LevelType,
): number => {
  const uniqueLevels = new Set<number>();

  const addIfValidLevel = (levelStr: string) => {
    const level = normalizeLevel(levelStr);
    if (
      levelStr &&
      levelStr !== LevelType.UNSPECIFIED &&
      level.type.toLowerCase() === levelType.toLowerCase() &&
      level.number !== undefined
    ) {
      uniqueLevels.add(level.number);
    }
  };

  // Process room levels
  rooms.forEach((room: Room) => {
    addIfValidLevel(room.ROOM_LEVEL || '');
  });

  // Process empty levels
  emptyLevels.forEach(addIfValidLevel);

  return findNextPositiveNumberFromArray(Array.from(uniqueLevels));
};

export const calculateRoomSummary = (
  rooms: Room[],
  emptyLevels: string[],
): RoomSummary => {
  const summary: RoomSummary = {
    levels: 0,
    totalFullBaths: 0,
    totalHalfBaths: 0,
    totalBedrooms: 0,
    totalRooms: 0,
    totalBathrooms: 0,
    storyList: ['Main'], // Initialize with Main by default
    upperLevels: {},
    lowerLevels: {},
    mainLevel: { bedrooms: 0, fullBaths: 0, halfBaths: 0, hasRoom: true },
  };

  // Initialize all levels with zero counts
  for (let i = 1; i <= 6; i++) {
    summary.upperLevels[i] = {
      bedrooms: 0,
      fullBaths: 0,
      halfBaths: 0,
      hasRoom: false,
    };
    summary.lowerLevels[i] = {
      bedrooms: 0,
      fullBaths: 0,
      halfBaths: 0,
      hasRoom: false,
    };
  }

  // Track unique levels for story list
  const uniqueUpperLevels = new Set<string>();
  const uniqueLowerLevels = new Set<string>();

  // Set Total Rooms
  summary.totalRooms = rooms.length;

  rooms.forEach((room) => {
    const { isBedroom, isFullBath, isHalfBath } = getRoomType(room.ROOM_TYPE);
    const rawLevel = room.ROOM_LEVEL || '';
    const level = normalizeLevel(rawLevel);

    if (
      rawLevel &&
      rawLevel.toLowerCase() !== LevelType.UNSPECIFIED.toLowerCase()
    ) {
      if (level.type === LevelType.UPPER) {
        uniqueUpperLevels.add(rawLevel);
      } else if (level.type === LevelType.LOWER) {
        uniqueLowerLevels.add(rawLevel);
      }
    }

    if (isBedroom) summary.totalBedrooms++;
    if (isFullBath) summary.totalFullBaths++;
    if (isHalfBath) summary.totalHalfBaths++;
    summary.totalBathrooms = summary.totalFullBaths + summary.totalHalfBaths;

    // Add to appropriate level counts
    if (level.type === LevelType.MAIN) {
      if (isBedroom) summary.mainLevel.bedrooms++;
      if (isFullBath) summary.mainLevel.fullBaths++;
      if (isHalfBath) summary.mainLevel.halfBaths++;
    } else if (level.number) {
      const counts =
        level.type === LevelType.UPPER
          ? summary.upperLevels[level.number]
          : summary.lowerLevels[level.number];

      // there is a room at this level, lets update the room counts
      counts.hasRoom = true;

      if (isBedroom) counts.bedrooms++;
      if (isFullBath) counts.fullBaths++;
      if (isHalfBath) counts.halfBaths++;
    }
  });

  emptyLevels.forEach((rawLevel) => {
    const level = normalizeLevel(rawLevel);

    if (
      rawLevel &&
      rawLevel.toLowerCase() !== LevelType.UNSPECIFIED.toLowerCase()
    ) {
      if (level.type === LevelType.UPPER) {
        uniqueUpperLevels.add(rawLevel);
      } else if (level.type === LevelType.LOWER) {
        uniqueLowerLevels.add(rawLevel);
      }
    }
  });

  // Calculate total levels (Main level is always counted)
  summary.levels = 1 + uniqueUpperLevels.size + uniqueLowerLevels.size;

  // Create sorted story list with Main always in the middle
  const upperLevels = Array.from(uniqueUpperLevels).sort((a, b) => {
    const numA = normalizeLevel(a).number ?? 0;
    const numB = normalizeLevel(b).number ?? 0;
    return numA - numB;
  });

  const lowerLevels = Array.from(uniqueLowerLevels).sort((a, b) => {
    const numA = normalizeLevel(a).number ?? 0;
    const numB = normalizeLevel(b).number ?? 0;
    return numA - numB;
  });

  summary.storyList = [...upperLevels, 'Main', ...lowerLevels];
  return summary;
};

/**
 * Check if all levels are expanded
 */
export const areAllLevelsExpanded = (
  expandedLevels: Record<string, boolean>,
): boolean => {
  return Object.values(expandedLevels).some((value) => value === true);
};

/**
 * Check if all levels are expanded
 */
export const checkExpandableLevels = (
  expandedLevels: Record<string, boolean>,
): boolean => {
  return Object.values(expandedLevels).some((value) => value === false);
};

export const getEmptyLevelKey = (
  level: LevelType,
  nextLevel: number,
  suffix: string = 'TOTAL_BATHS', // TOTAL_BATHS, HALF_BATHS, FULL_BATHS, BEDROOMS
): string => {
  return `${level.toUpperCase()}_LEVEL_${nextLevel}_${suffix}`;
};

export const getEmptyLevelKeys = (
  level: LevelType,
  nextLevel: number,
  value: number | null,
): Record<string, number | null> => {
  const halfBathsKey = getEmptyLevelKey(level, nextLevel, 'HALF_BATHS');
  const fullBathsKey = getEmptyLevelKey(level, nextLevel, 'FULL_BATHS');
  const bedroomsKey = getEmptyLevelKey(level, nextLevel, 'BEDROOMS');

  return {
    [halfBathsKey]: value,
    [fullBathsKey]: value,
    [bedroomsKey]: value,
  };
};

export const generateLevels = (levelType: LevelType, maxLevel: number) => {
  return Array.from({ length: maxLevel }, (_, i) => i + 1).map((i) => {
    return `${levelType} ${i}`;
  });
};

export const getMaxLevels = (levels: string[]) => {
  let upperMaxLevel = 0;
  let lowerMaxLevel = 0;
  levels.forEach((level) => {
    const normalizedLevel = normalizeLevel(level);
    if (normalizedLevel.type === LevelType.UPPER && normalizedLevel.number) {
      upperMaxLevel = Math.max(normalizedLevel.number, upperMaxLevel);
    }

    if (normalizedLevel.type === LevelType.LOWER && normalizedLevel.number) {
      lowerMaxLevel = Math.max(normalizedLevel.number, lowerMaxLevel);
    }
  });

  return {
    upperMaxLevel,
    lowerMaxLevel,
  };
};

export const getMaxRoomType = (
  roomType: string,
  roomTypes: string[] = [],
): string => {
  const normalizedRoomType = normalizeRoomType(roomType);
  const isNumberedRoomType = normalizedRoomType.isNumberedRoomType;

  if (!isNumberedRoomType) {
    return roomType;
  }

  const filteredRoomNumbers = roomTypes.reduce(
    (roomNumbers, localRoomType): number[] => {
      if (!localRoomType.includes(normalizedRoomType.type)) {
        return roomNumbers;
      }

      const _normalizedType = normalizeRoomType(localRoomType);
      const roomNumber = _normalizedType.number;

      if (roomNumber && roomNumber >= 1 && !roomNumbers.includes(roomNumber)) {
        return [...roomNumbers, roomNumber];
      }

      return roomNumbers;
    },
    [] as number[],
  );

  const nextRoomType = findNextPositiveNumberFromArray(filteredRoomNumbers);

  // special case for Bathroom since there is no generic type
  if (normalizedRoomType.type === 'Bathroom' && nextRoomType >= 4) {
    return 'Full Bath';
  }

  return `${normalizedRoomType.type} ${nextRoomType}`;
};
